import { useIntl } from 'react-intl';
import { ResponsiveLine } from '@nivo/line';
import { Popover, Typography } from 'antd';
import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';
import { ContentWrapper, EllipsisButton, Group } from '@/components/_common';

import { useChartTheme, useConvertChartData, useDisclosure } from '@/hooks';

import moment, { Moment } from 'moment';
import InvoicingAverageTicketModal from './InvoicingAverageTicketModal';
import { useChartAverageTicket } from '@/core/domain/workorder/ManagementCharts/ManagementChart.hooks';
import { useEffect } from 'react';
import { DateTimeFormat } from '@/utils/moment';
import { WorkOrderComplementType } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { AverageTicketParams } from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';
import { Info } from 'react-feather';

const { Title, Paragraph } = Typography;

export interface ChartInvoicingAverageTicketFilterValues {
  companiesIds: number[];
  types: WorkOrderComplementType;
  initialDate: Moment;
  finalDate: Moment;
}

const defaultStartDate = moment().subtract(5, 'months').startOf('day');
const defaultEndDate = moment().endOf('day');

function InvoicingAverageTicket() {
  const intl = useIntl();
  const chartTheme = useChartTheme();
  const [modalVisible, modalVisibleAction] = useDisclosure();

  const chartAverageTicket = useChartAverageTicket();

  const chartData = useConvertChartData(chartAverageTicket.data?.chartData);

  useEffect(() => {
    chartAverageTicket.mutateAsync({
      startDate: defaultStartDate.startOf('month').format(DateTimeFormat),
      endDate: defaultEndDate.endOf('day').format(DateTimeFormat),
      types: WorkOrderComplementType.WorkOrder,
    });
  }, []);

  const handleOk = async (values: ChartInvoicingAverageTicketFilterValues) => {
    try {
      const obj: AverageTicketParams = {
        ...values,
        startDate: values.initialDate.format(DateTimeFormat),
        endDate: values.finalDate.format(DateTimeFormat),
      };

      await chartAverageTicket.mutateAsync(obj);

      modalVisibleAction.close();
    } catch {}
  };

  const items = [
    {
      label: 'Ticket Médio',
      desc: '[(Total OS – Total peças) + Total VD] / Passagem (Quantidade OS fechadas + Quantidade VD autorizadas)',
    },
    {
      label: 'Ticket Médio OS',
      desc: '(Total da OS - Total peças fornecidas) / Passagens OS (Quantidade OS fechadas)',
    },
    {
      label: 'Ticket Médio VD',
      desc: 'Total VD / Passagens VD (Quantidade VD autorizadas)',
    },
    {
      label: 'Ticket Médio Peças',
      desc: 'Total peças - Peças fornecidas / Passagens OS (Quantidade OS fechadas)',
    },
    {
      label: 'Ticket Médio Serviços',
      desc: 'Total serviços / Passagens OS (Quantidade OS fechadas)',
    },
    {
      label: 'Ticket Médio Serviços terceiros',
      desc: 'Total serviços terceiros / Passagens OS (Quantidade OS fechadas)',
    },
  ];

  return (
    <>
      <ContentWrapper
        style={{ height: '430px', padding: '8px' }}
        extra={
          <Group>
            <Popover
              title="Ticket Médio (Peças x Serviços)"
              placement="right"
              content={
                <div>
                  {items.map((item, index) => (
                    <p key={index}>
                      <strong>{item.label}:</strong> {item.desc}
                    </p>
                  ))}
                </div>
              }
            >
              <Info size={18} color="#364068" />
            </Popover>

            <EllipsisButton onClick={modalVisibleAction.open} />
          </Group>
        }
      >
        <Title level={5} style={{ textAlign: 'center', marginBottom: '4px' }}>
          Ticket Médio (Peças x Serviços)
        </Title>
        <Paragraph style={{ textAlign: 'center', margin: '4px 0 4px 0' }}>
          {`Período: ${moment(chartAverageTicket.variables?.startDate).format(
            'DD/MM/YYYY',
          )} a ${moment(chartAverageTicket.variables?.endDate).format(
            'DD/MM/YYYY',
          )}`}
        </Paragraph>
        <ResponsiveLine
          data={chartData ? chartData : []}
          enableSlices="x"
          margin={{ top: 15, right: 25, bottom: 90, left: 70 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            reverse: false,
          }}
          yFormat={(value) => `${currencyFormatter(value)}`}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 7,
            tickPadding: 10,
            tickRotation: 0,
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: 5,
            legend: intl.formatMessage({
              id: 'dashboard.averageTicketReport.averageTicketPartsAndServices',
            }),
            format: (value) => `${metricPrefixFormatter(+value)}`,
            legendOffset: -50,
            legendPosition: 'middle',
          }}
          enableGridX={false}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh
          theme={chartTheme}
        />
      </ContentWrapper>

      <InvoicingAverageTicketModal
        open={modalVisible}
        handleOk={handleOk}
        onCancel={modalVisibleAction.close}
      />
    </>
  );
}

export default InvoicingAverageTicket;
